import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { errorMessage } from './errors';

const LABEL = 'text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1';
const URL_RE = /^https?:\/\/\S+$/i;

// Point a webhook at a URL and pick which events it gets. `events` is the
// server's list; `onSubmit({ url, events })` is async and throws on failure (shown inline).
export default function WebhookDialog({ open, events, onClose, onSubmit }) {
  const [url, setUrl] = useState('');
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setUrl(''); setPicked(events); setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (e, on) => setPicked(prev => (on ? [...prev, e] : prev.filter(x => x !== e)));

  const submit = async () => {
    const u = url.trim();
    if (!URL_RE.test(u)) { setError('Enter a full URL starting with http:// or https://'); return; }
    if (picked.length === 0) { setError('Pick at least one event'); return; }
    setSaving(true);
    try { await onSubmit({ url: u, events: events.filter(e => picked.includes(e)) }); }
    catch (e) { setError(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="webhook-dialog">
        <DialogHeader>
          <DialogTitle>Add webhook</DialogTitle>
          <DialogDescription>We POST the events you pick to this URL, signed with a secret you get once after saving.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label htmlFor="wh-url" className={LABEL}>Endpoint URL</label>
            <Input id="wh-url" data-testid="wh-url" value={url} placeholder="https://example.com/hooks/loan-officer"
              onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div>
            <span className={LABEL}>Events</span>
            <ul className="space-y-1" data-testid="wh-events">
              {events.map(e => (
                <li key={e}>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox data-testid={`wh-event-${e}`} checked={picked.includes(e)} onCheckedChange={(c) => toggle(e, c === true)} />
                    <code className="text-xs">{e}</code>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          {error && <p role="alert" data-testid="wh-error" className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button type="button" data-testid="wh-create" onClick={submit} disabled={saving} className="btn-gold">{saving ? 'Adding…' : 'Add webhook'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
